import { supabase } from "../supabase/SupabaseClient";
import { FLOTABOT } from "../components/layout/secciones";

/* Pedirle al CENT la RTO de UNA patente ahora, sin esperar a la noche.
 *
 * Aldo, 09/10 14:18: *«recién hicimos la VTV del furgón 448 y no aparece
 * habilitada en FIBOT»*. El 448 (KPT376) se había consultado a las 04:35,
 * horas antes de la revisión, y el hub iba a seguir mostrando lo de esa
 * madrugada hasta la pasada siguiente. Igna: *«cuando te dicen algo así que se
 * puede resolver en el momento, hacelo y fue»* (ticket #371).
 *
 * ── VA POR FLOTABOT Y NO DIRECTO AL CENT ──────────────────────────────────
 *
 * El hub es estático y el navegador no le puede hablar al CENT (otro dominio,
 * sin CORS). Pero aunque pudiera no se haría: el que sabe consultarlo, dejar
 * la respuesta en la base y frenar al que aprieta dos veces es el bot
 * (`rto_cent.consultar_ahora`). Acá sólo se pide y se muestra. Lo que vuelve
 * queda guardado, así que la portería y el bot leen lo mismo que esta pantalla.
 *
 * ── EL CONTRATO (cualquier cambio acá va también en `app.py` del bot) ─────
 *
 *     POST {FLOTABOT}/api/rto/consultar
 *     Authorization: Bearer <token de Supabase>
 *     {"dominio": "KPT376"}   ó   {"interno": "448", "tipo": "furgon"}
 *
 *     200  {ok: true, dominio, estado, consultado_en, novedad}
 *     429  {ok: false, error, dominio, estado, consultado_en, reintentar_en}
 *          — esa patente se consultó hace menos de cinco minutos, ya se
 *            hicieron las consultas de la hora, o hay otras esperando al
 *            CENT. Las tres se muestran igual: el texto del bot dice cuál.
 *     502  {ok: false, error, dominio, estado, consultado_en}
 *          — el CENT no contestó
 *     400 / 404 / 422  {ok: false, error} — el pedido no sirve
 *     401 sin sesión · 403 sin permiso · 503 no se pudo (base o padrón)
 *
 * `estado` es la misma fila que devuelve `rto_estado()`, así que la pantalla
 * la pega encima de la suya con `soloCampos` sin recalcular nada. En 429 y
 * 502 es el que había: se sigue mostrando, no se borra.
 *
 * El token va en el encabezado, como en `responderComoBot` y `guiaQueMeToca`:
 * el bot saca el mail de ahí y chequea el permiso. No se manda ningún mail en
 * el cuerpo — sería algo que se puede falsear desde la consola.
 *
 * El `Retry-After` del 429 no se lee: no es un encabezado que el navegador
 * deje ver en un pedido a otro dominio si el servidor no lo expone. Los
 * segundos vienen también en el cuerpo (`reintentar_en`), y de ahí se toman.
 *
 * ── UN MINUTO, NO MÁS ─────────────────────────────────────────────────────
 *
 * El navegador no le pone plazo a un `fetch`, y del lado del bot gunicorn
 * corta recién a los tres minutos. Lo normal es que el CENT conteste en unos
 * cuatro segundos y el bot no lo espera más de quince; si al minuto no volvió
 * nada, algo se colgó en el medio, y dejar el botón en «Consultando…» —con
 * los de las otras filas apagados, ver `ConsultarCENT.jsx`— no le sirve a
 * nadie. Se corta y se dice qué hacer. Si el bot igual terminaba, lo que
 * trajo quedó guardado y aparece al recargar.
 *
 * ── NUNCA LANZA ───────────────────────────────────────────────────────────
 *
 * Devuelve siempre `{ ok, codigo, dominio, estado, consultadoEn, novedad,
 * error, reintentarEn }`. Quien lo llama es un botón en una fila de 658, y un
 * error sin atrapar ahí se llevaría puesta la tabla entera por un sitio ajeno
 * que estaba lento.
 */

export const RUTA_CONSULTAR = "/api/rto/consultar";

export const PLAZO_MS = 60 * 1000;

const SIN_SESION = "Se cerró la sesión. Entrá de nuevo y probá otra vez.";
const NO_CONECTA = "No se pudo conectar con FlotaBot. Probá de nuevo en un minuto.";
const TARDO =
  "FlotaBot tardó demasiado en contestar. Si el CENT respondió, el dato aparece al recargar en unos minutos.";

function respuesta(extra) {
  return {
    ok: false,
    codigo: null,
    dominio: null,
    estado: null,
    consultadoEn: null,
    novedad: false,
    error: null,
    reintentarEn: null,
    ...extra,
  };
}

/* Lo que se le manda al bot. Un texto es una patente; si no, el objeto tal
 * cual con sus dos formas (`dominio`, o `interno` + `tipo`). Lo que no es
 * ninguna de las dos no sale. */
function cuerpoDe(que) {
  if (typeof que === "string") {
    const d = que.trim();
    return d ? { dominio: d } : null;
  }
  if (!que || typeof que !== "object") return null;
  const dominio = String(que.dominio ?? "").trim();
  if (dominio) return { dominio };
  const interno = String(que.interno ?? "").trim();
  if (!interno) return null;
  return que.tipo ? { interno, tipo: String(que.tipo) } : { interno };
}

/* «No podés» y «no pude» no son lo mismo, y cada código dice cuál. El texto
 * del bot manda cuando lo hay: es el que sabe qué pasó. La excepción es el
 * 403, que del bot sale con la frase genérica de la puerta. */
function errorDe(status, j) {
  const delBot = j && typeof j.error === "string" && j.error.trim() ? j.error : null;
  if (status === 403) return "No tenés permiso para consultar la RTO.";
  if (status === 401)
    return delBot || "FlotaBot no reconoció la sesión. Recargá la página y probá otra vez.";
  if (status === 429)
    return delBot || "Ahora no se puede consultar el CENT. Probá de nuevo en unos minutos.";
  if (status === 502)
    return delBot || "El CENT no contestó. Probá de nuevo en unos minutos.";
  if (!j) return "FlotaBot no contestó bien. Probá de nuevo en un rato.";
  return delBot || "No se pudo consultar. Probá de nuevo en un rato.";
}

export async function consultarCENT(que) {
  const cuerpo = cuerpoDe(que);
  if (!cuerpo) return respuesta({ error: "Falta la patente." });

  let token = null;
  try {
    const { data } = await supabase.auth.getSession();
    token = data?.session?.access_token || null;
  } catch {
    token = null;
  }
  if (!token) return respuesta({ codigo: 401, dominio: cuerpo.dominio || null, error: SIN_SESION });

  /* El plazo cubre el pedido Y la lectura del cuerpo: un bot que contesta
   * los encabezados y se cuelga mandando el resto también deja esperando. */
  const corte = typeof AbortController === "function" ? new AbortController() : null;
  let vencio = false;
  const reloj = setTimeout(() => {
    vencio = true;
    if (corte) corte.abort();
  }, PLAZO_MS);

  let r;
  let j = null;
  try {
    try {
      r = await fetch(`${FLOTABOT}${RUTA_CONSULTAR}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(cuerpo),
        ...(corte ? { signal: corte.signal } : {}),
      });
    } catch {
      /* Red, CORS, o el bot reiniciando. Para el que apretó el botón son lo
       * mismo: no pasó nada y puede volver a probar. */
      return respuesta({
        codigo: 0,
        dominio: cuerpo.dominio || null,
        error: vencio ? TARDO : NO_CONECTA,
      });
    }

    try {
      j = await r.json();
    } catch {
      j = null;
    }
    if (vencio) {
      return respuesta({ codigo: 0, dominio: cuerpo.dominio || null, error: TARDO });
    }
  } finally {
    clearTimeout(reloj);
  }
  if (j && typeof j !== "object") j = null;

  const base = {
    codigo: r.status,
    dominio: (j && typeof j.dominio === "string" && j.dominio) || cuerpo.dominio || null,
    estado: j && j.estado && typeof j.estado === "object" ? j.estado : null,
    consultadoEn: (j && typeof j.consultado_en === "string" && j.consultado_en) || null,
  };

  /* UN 200 QUE NO ES JSON NO ES UN ÉXITO. Es el redirect al login con cara de
   * respuesta (`leerOt` lo aprendió primero): sin este chequeo la pantalla
   * diría «consultado» sin que nadie le haya preguntado nada al CENT. */
  if (r.ok && j && j.ok === true) {
    return respuesta({ ...base, ok: true, novedad: j.novedad === true });
  }

  const espera = Number(j?.reintentar_en);
  return respuesta({
    ...base,
    error: errorDe(r.status, j),
    reintentarEn: Number.isFinite(espera) && espera > 0 ? Math.ceil(espera) : null,
  });
}

/* Cuándo se le preguntó al CENT por esta patente por última vez.
 *
 * Es lo que le faltaba a la ficha para que el caso del 448 se entienda solo:
 * «consultado hoy 04:35» al lado de una revisión que se hizo a las 13 dice por
 * qué no aparece, y el botón de al lado dice qué hacer.
 *
 * `rto_consulta` se lee con la misma política que `rto` (la ve quien ve la
 * RTO). Devuelve `{ consultado, error }` o `null`, y `null` cubre «nunca» y
 * «no pude leerlo»: la ficha en los dos casos no dice nada de la hora, que es
 * mejor que inventar una. Nunca lanza — es un agregado y no puede tirar abajo
 * el bloque de la RTO.
 */
export async function ultimaConsultaCENT(dominio) {
  const limpio = String(dominio || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!limpio) return null;
  try {
    const { data, error } = await supabase
      .from("rto_consulta")
      .select("consultado, error")
      .eq("dominio", limpio)
      .limit(1);
    if (error) return null;
    const fila = data?.[0];
    if (!fila?.consultado) return null;
    return { consultado: fila.consultado, error: fila.error || null };
  } catch {
    return null;
  }
}
